import React, { useCallback, useEffect, useState } from 'react'
import { BUTTON_THEME, CircularLoader as Loader } from '@st-operations/uikit/src/v2'

import Header from '_components/header'
import {
  createPush,
  getPartners,
  getPush,
  runPush as runPushRequest,
  updatePush,
} from '_services/api-integrations'
import { getTaxonomies } from '_services/taxonomies'

import { NO_TAXONOMY_SELECTION, POLL_EVERY_MS } from '../constants'
import PushForm from './form'
import PushStatus from './status'
import '../styles.scss'

const EMPTY = {
  partner: '',
  mapping_id: '',
  settings: {},
  parent_node: null,
  name_changes: [],
  selection: { mode: 'all' },
  status: 'draft',
}

// A push started from the sidebar starts with no taxonomy, until one is chosen
const EMPTY_NO_TAXONOMY = { ...EMPTY, taxonomy: null, selection: NO_TAXONOMY_SELECTION }

// One URL for a push, rendered by status. A draft shows the form; anything
// that has run shows what happened. So the page you were on when you hit Run
// becomes the status screen, and stays valid afterwards as the record of it.
//
// Opened from a taxonomy's page, the push is for that taxonomy. Opened from
// the sidebar's list of every push, the taxonomy is chosen on the form - or
// left out, for audiences listed by mapping ID from any taxonomy.
const Push = (props) => {
  const { slug, id } = props.match.params
  const isNew = !id || id === 'new'
  const fromSidebar = !slug
  const base = fromSidebar ? '/taxonomy-api-integrations' : `/taxonomies/${slug}/api-integrations`

  const [taxonomy, setTaxonomy] = useState(null)
  const [push, setPush] = useState(fromSidebar ? EMPTY_NO_TAXONOMY : EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // for the partner's name, rather than its slug
  const [partners, setPartners] = useState([])

  useEffect(() => {
    getPartners()
      .then((data) => setPartners(data?.items || []))
      .catch(() => setPartners([]))
  }, [])

  const reload = useCallback(async () => {
    if (isNew) return
    try {
      const found = await getPush(id)
      setPush(found)
    } catch (e) {
      setError(e?.message || 'could not load that push')
    }
  }, [id, isNew])

  useEffect(() => {
    let cancelled = false

    const start = async () => {
      try {
        if (!fromSidebar) {
          const found = await getTaxonomies({ slug, limit: 1 })
          const item = found?.items?.[0]
          if (cancelled) return
          if (!item) {
            setError(`no taxonomy called "${slug}"`)
            setLoading(false)
            return
          }
          setTaxonomy(item)
        }
        if (!isNew) await reload()
      } catch (e) {
        if (!cancelled) setError(e?.message || 'could not load')
      }
      if (!cancelled) setLoading(false)
    }

    start()
    return () => {
      cancelled = true
    }
  }, [slug, isNew, reload, fromSidebar])

  // while it is running, keep the counters and the heartbeat fresh
  useEffect(() => {
    if (push?.status !== 'running') return undefined
    const timer = setInterval(reload, POLL_EVERY_MS)
    return () => clearInterval(timer)
  }, [push?.status, reload])

  // the page's taxonomy, or - from the sidebar - the one chosen on the form
  const pushTaxonomy = fromSidebar ? push?.taxonomy || null : taxonomy

  const persist = async () => {
    const payload = {
      ...push,
      taxonomy: pushTaxonomy && {
        _id: pushTaxonomy._id,
        name: pushTaxonomy.name,
        slug: pushTaxonomy.slug,
      },
    }
    if (push._id) {
      return updatePush(push._id, payload)
    }
    return createPush(payload)
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const saved = await persist()
      setPush(saved)
      // a brand new draft now has a URL of its own
      if (!id || id === 'new') {
        props.history.replace(`${base}/${saved._id}`)
      }
    } catch (e) {
      setError(e?.message || 'could not save that')
    }
    setSaving(false)
  }

  const run = async () => {
    setSaving(true)
    setError(null)
    try {
      // always save first, so what runs is exactly what is on screen
      const saved = await persist()
      await runPushRequest(saved._id)
      const started = await getPush(saved._id)
      setPush(started)
      if (!id || id === 'new') {
        props.history.replace(`${base}/${saved._id}`)
      }
    } catch (e) {
      setError(e?.message || 'could not start that push')
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <div className="api-integrations-loading">
        <Loader />
      </div>
    )
  }

  const isDraft = !push?.status || push.status === 'draft'

  const partner = partners.find((p) => p.slug === push?.partner) || null
  const partnerName = partner?.name || push?.partner
  // the taxonomy is already in the title, so the push's own name - which
  // repeats it, with a timestamp - is not used here
  const pushTitle = isNew
    ? 'New push'
    : [partnerName, push?.mapping_id].filter(Boolean).join(' · ') || 'Push'

  // from the sidebar the title has no taxonomy in it, so the push says which
  let title = taxonomy ? `${taxonomy.name} > API Integrations > ${pushTitle}` : pushTitle
  if (fromSidebar) {
    const which = isNew ? pushTitle : `${push?.taxonomy?.name || 'No taxonomy'} · ${pushTitle}`
    title = `Taxonomy API Integrations > ${which}`
  }

  return (
    <div className="api-integrations">
      {/* the app's fixed top bar, as on the other taxonomy pages */}
      <Header
        title={title}
        actions={[
          {
            // back to the list this push was opened from
            children: fromSidebar ? '← Taxonomy API Integrations' : '← API Integrations',
            theme: BUTTON_THEME.GHOST,
            onClick: () => props.history.push(base),
          },
        ]}
      />

      <Choose>
        <When condition={isDraft}>
          <PushForm
            taxonomy={pushTaxonomy}
            chooseTaxonomy={fromSidebar}
            push={push}
            onChange={setPush}
            onSave={save}
            onRun={run}
            saving={saving}
            error={error}
            prefill={isNew ? props.location?.state?.prefill || null : null}
          />
        </When>
        <Otherwise>
          <PushStatus
            push={push}
            partner={partner}
            partnerName={partnerName}
            onChanged={reload}
            onOpenPush={(pushId) => props.history.push(`${base}/${pushId}`)}
          />
        </Otherwise>
      </Choose>
    </div>
  )
}

export default Push
